/**
 * Sección "Por dónde te entran" — estadística por CANAL de compartir. El promotor
 * ve por qué vía llegó cada jugador (WhatsApp/redes/Telegram/QR/copiar) y cuántos
 * convirtieron. Le emociona analizar qué canal le rinde mejor para optimizar.
 * Se monta en Campañas. Carga GET /api/cashier/canales. Read-only.
 */
import { t } from '../languages.js';

// Mapeo canal técnico → { label i18n, ícono, color }. El canal viene de utm_medium
// (lo setea la landing al decodificar el código del link).
const CANAL_INFO = {
    wa_lista: { key: 'canales.waLista', color: '#25d366', icon: iconWa },
    wa_num: { key: 'canales.waNum', color: '#25d366', icon: iconWa },
    redes: { key: 'canales.redes', color: '#d6249f', icon: iconRedes },
    telegram: { key: 'canales.telegram', color: '#229ED9', icon: iconTelegram },
    qr: { key: 'canales.qr', color: '#6a5cff', icon: iconQr },
    copy: { key: 'canales.copy', color: '#f5c451', icon: iconCopy },
    directo: { key: 'canales.directo', color: '#8595b0', icon: iconLink },
};

function infoCanal(canal) {
    return CANAL_INFO[canal] || { key: null, label: canal, color: '#8595b0', icon: iconLink };
}

/**
 * Devuelve el contenedor de la sección. Recibe apiGet + los inputs de fecha que ya
 * tiene la vista Campañas (comparte el mismo rango).
 * @param {{ apiGet:(url:string)=>Promise<any>, getRango:()=>{desde:string,hasta:string} }} ctx
 * @returns {{ el:HTMLElement, recargar:()=>void }}
 */
export function renderSeccionCanales({ apiGet, getRango }) {
    const el = document.createElement('div');
    el.className = 'card';
    el.style.cssText = 'margin-bottom:10px;';
    el.innerHTML = `
        <h2>${t('canales.titulo')}</h2>
        <p style="color:var(--text-dim); font-size:15px; margin:0 0 10px; line-height:1.35;">${t('canales.descripcion')}</p>
        <div id="pm-canales-body">
            <div style="text-align:center; color:var(--text-dim); padding:16px;">${t('common.cargando')}</div>
        </div>`;

    const body = el.querySelector('#pm-canales-body');

    async function recargar() {
        const { desde, hasta } = getRango();
        const r = await apiGet(`/api/cashier/canales?desde=${desde}&hasta=${hasta}`);
        if (!r || r.ok === false) {
            body.innerHTML = `<div style="color:var(--text-dim); font-size:14px;">${t('common.reintentar')}</div>`;
            return;
        }
        const canales = (r.canales || []).filter(c => (c.captados || 0) > 0);
        if (canales.length === 0) {
            body.innerHTML = `<div style="color:var(--text-dim); font-size:15px; padding:8px 0;">${t('canales.vacio')}</div>`;
            return;
        }
        const maxCaptados = Math.max(...canales.map(c => c.captados || 0), 1);
        body.innerHTML = canales
            .map(c => {
                const info = infoCanal(c.canal);
                const label = info.key ? t(info.key) : info.label;
                const captados = c.captados || 0;
                const pctBarra = Math.round((captados / maxCaptados) * 100);
                // Conversión por canal = recargó (puso plata), no jugó el bono.
                const pctConv = captados > 0 ? Math.round(((c.con_recarga || 0) / captados) * 100) : 0;
                return `
                <div class="pm-canal-row">
                    <span class="pm-canal-icon" style="color:${info.color};">${info.icon()}</span>
                    <div class="pm-canal-info">
                        <div class="pm-canal-nombre">${label}</div>
                        <div class="pm-canal-barra"><div class="pm-canal-fill" style="width:${pctBarra}%; background:${info.color};"></div></div>
                    </div>
                    <div class="pm-canal-cifras">
                        <div class="pm-canal-captados">${captados}</div>
                        <div class="pm-canal-conv">${pctConv}% ${t('canales.recargan')}</div>
                    </div>
                </div>`;
            })
            .join('');
    }

    recargar();
    return { el, recargar };
}

// ── Íconos (mismos del compartir, en chico) ─────────────────────────────
function iconWa() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1.1 2.8 1.2 3c.1.2 2.1 3.2 5 4.5.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.2-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2z"/></svg>';
}
function iconTelegram() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M21.94 4.39 18.6 20.1c-.25 1.1-.9 1.38-1.83.86l-5.06-3.73-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.16L17.4 6.5c.41-.36-.09-.56-.63-.2L6.4 13.04l-4.94-1.55c-1.07-.34-1.1-1.07.22-1.59L20.55 2.5c.9-.33 1.68.2 1.39 1.89z"/></svg>';
}
function iconRedes() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/></svg>';
}
function iconQr() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/></svg>';
}
function iconCopy() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
}
function iconLink() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5"/></svg>';
}
